import { Store } from 'redux'
import { Entity } from '../app/entity'
import TimeRange from '../app/timeRange'
import { ApplicationState } from '../redux/ApplicationState'
import { SERVICE_ALL, ServiceType } from '../domain/Service'
import { monitorServiceEnvironmentKey, MonitorState } from '../redux/monitor/MonitorState'
import { produce } from 'immer'

/**
 * Loader classes like this consilidate all loading monitors for a domain
 * concept.
 *
 * They define one (or multiple) load() methods which execute an AJAX call
 * through the Api. You may implement parameter remapping and cache checks in
 * here. For the loaded date there should be a dispatched action through Redux.
 *
 * The Loader also defines a (static) method which handles its own actions and
 * applies the corresponding changes to the global store state.
 */
const Loader = function(store: Store<ApplicationState>, api) {
    this.store = store
    this.api = api

    this.loadGraph = parameters => {
        const range = TimeRange.fromParameters(parameters)

        if (parameters.s && parameters.s === SERVICE_ALL) {
            const { tidewaysContext } = store.getState()
            const isExternalService = tidewaysContext.route.parameters?.st === ServiceType.external
            const services = tidewaysContext.getServicenames(isExternalService)

            services.forEach(serviceName => {
                this.api.triggerContinuously('XhprofPerformanceBundle.Api.graph', { ...parameters, s: serviceName }, range.getCacheKey())
            })

            return
        }

        this.api.triggerContinuously('XhprofPerformanceBundle.Api.graph', parameters, range.getCacheKey())
    }

    this.loadBaseServiceHistoryRange = parameters => {
        this.api.triggerContinuously(
            'XhprofPerformanceBundle.Api.baseServiceHistoryRange',
            parameters,
            parameters.organization + '/' + parameters.application,
        )
    }

    this.loadMyProfiles = parameters => {
        const range = TimeRange.fromParameters(parameters)

        parameters.sortBy = parameters.sort || 'time'
        parameters.minDate = range.getStartDate().format('YYYY-MM-DD HH:mm')
        parameters.maxDate = range.getEndDate().format('YYYY-MM-DD HH:mm')

        this.api.triggerContinuously('XhprofProfilerBundle.Profile.listMine', parameters)
    }

    this.loadCriticalOperations = parameters => {
        this.api.triggerContinuously('XhprofProfilerBundle.Transaction.critical', parameters)
    }
}

const initialGlobalState: MonitorState = {
    baseServiceHistory: {},
    graph: {},
    profiles: null,
    operations: null,
}

Loader.handleAction = (globalState: MonitorState = initialGlobalState, action) => {
    let clonedGlobalState = {}

    switch (action.type) {
        case 'TIDEWAYS_ROUTE':
            return {
                baseServiceHistory: globalState.baseServiceHistory,
                graph: {},
                profiles: Entity.purge(globalState.profiles ?? new Entity()),
                operations: Entity.purge(globalState.operations ?? new Entity()),
            }
        case 'Api.graph.success':
            return produce(globalState, draft => {
                const environment = action.data.report.criteria.environment
                const service = action.data.report.criteria.service
                const serviceEnvironment = monitorServiceEnvironmentKey(environment, service)

                if (!(serviceEnvironment in draft.graph)) {
                    draft.graph[serviceEnvironment] = {}
                }

                draft.graph[serviceEnvironment][action.id] = new Entity(action.data, 60)
            })

        case 'Api.graph.error':
            return produce(globalState, draft => {
                // we do not know env + service here, so clear all services without any data yet
                Object.keys(globalState.graph).forEach(serviceEnvironmentKey => {
                    if (!(action.id in draft.graph[serviceEnvironmentKey])) {
                        draft.graph[serviceEnvironmentKey][action.id] = new Entity().setError(action.error)
                    }
                })
            })

        case 'Api.baseServiceHistoryRange.success':
            clonedGlobalState = { ...globalState.baseServiceHistory }
            clonedGlobalState[action.id] = new Entity(action.data.history, 60)

            return {
                ...globalState,
                baseServiceHistory: clonedGlobalState,
            }
        case 'Api.baseServiceHistoryRange.error':
            clonedGlobalState = { ...globalState.baseServiceHistory }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                baseServiceHistory: clonedGlobalState,
            }

        case 'Profile.listMine.success':
            return {
                ...globalState,
                profiles: new Entity(action.data, 60),
            }
        case 'Profile.listMine.error':
            return {
                ...globalState,
                profiles: new Entity().setError(action.error),
            }
        case 'Transaction.critical.success':
            return {
                ...globalState,
                operations: new Entity(action.data, 60),
            }
        case 'Transaction.critical.error':
            return {
                ...globalState,
                operations: new Entity().setError(action.error),
            }
    }

    return globalState
}

export default Loader
